/**
 * waap-api-protection-tab.tsx — API Protection tab (B4 batch).
 *
 * 5-section view rendered as inner sub-tabs:
 *   overview     — /api-protection/overview/charts (read-only)
 *   discovery    — /api-protection/discovery/rules (read-only list)
 *   threat       — /api-protection/threat-detection/charts (read-only)
 *   dlp          — /api-protection/data-loss/rules (GET + PUT per rule)
 *   vulnerability— /api-protection/vulnerability/overview/charts (read-only)
 *
 * Only DLP rules are mutable (PUT per rule config). All mutations via
 * ActAsMutationConfirm. Sections without mutations are read-only.
 *
 * Spec note: Discovery config + threat rules + vulnerability rules endpoints
 * exist but are not exposed on the public Open-API (private SSO). The sections
 * render their charts/lists; detail mutations are noted as unavailable.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield } from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardBody,
  Button, Badge, Skeleton, Textarea,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { TabError } from '@/pages/customers/detail/tab-states';
import { ActAsMutationConfirm } from '@/pages/operations/detail/cdn/act-as-mutation-confirm';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';
import {
  useActAsWAAPApiProtectionOverviewCharts,
  useActAsWAAPApiDiscoveryRules,
  useActAsWAAPVulnerabilityCharts,
  useActAsWAAPDlpRules,
  type DlpRule,
  type DlpRuleConfig,
} from '@/api/act-as-waap-rules';

/* ── Overview section ────────────────────────────────────────────────────── */

function OverviewSection({ anchorId }: { anchorId: string }) {
  const { t } = useTranslation();
  const chartsQ = useActAsWAAPApiProtectionOverviewCharts(anchorId);

  if (chartsQ.isLoading) return <Skeleton style={{ width: '100%', height: 100 }} />;
  if (chartsQ.isError) {
    return (
      <TabError
        title={t('waap_domain.api_prot_overview_error', 'Failed to load API protection overview')}
        onRetry={() => chartsQ.refetch()}
      />
    );
  }

  const charts = chartsQ.data?.charts ?? [];

  if (charts.length === 0) {
    return (
      <EmptyState
        title={t('waap_domain.api_prot_overview_empty', 'No overview data')}
        description={t('waap_domain.api_prot_overview_empty_desc', 'API protection overview charts are not yet available for this account.')}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        {t('waap_domain.api_prot_overview_count', '{{n}} chart(s) loaded.', { n: charts.length })}
      </p>
      <Alert tone="info" style={{ fontSize: 'var(--vn-font-size-xs)' }}>
        {t('waap_domain.api_prot_charts_note', 'Chart rendering requires the Partner console chart engine. This view shows raw counts only.')}
      </Alert>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--vn-space-3)' }}>
        {charts.map((c, i) => {
          const chart = c as Record<string, unknown>;
          return (
            <Card key={String(chart.key ?? i)}>
              <CardBody>
                <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginBottom: 'var(--vn-space-1)' }}>
                  {String(chart.title ?? chart.key ?? '')}
                </div>
                <div style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 600 }}>
                  {chart.kind === 'number'
                    ? String(chart.value ?? '—')
                    : String(chart.kind ?? 'chart')}
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ── Discovery section ───────────────────────────────────────────────────── */

function DiscoverySection({ anchorId }: { anchorId: string }) {
  const { t } = useTranslation();
  const rulesQ = useActAsWAAPApiDiscoveryRules(anchorId);

  if (rulesQ.isLoading) return <Skeleton style={{ width: '100%', height: 80 }} />;
  if (rulesQ.isError) {
    return (
      <TabError
        title={t('waap_domain.api_prot_discovery_error', 'Failed to load discovery rules')}
        onRetry={() => rulesQ.refetch()}
      />
    );
  }

  const rules = rulesQ.data ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <Alert tone="info" style={{ fontSize: 'var(--vn-font-size-xs)' }}>
        {t('waap_domain.api_prot_discovery_note', 'API discovery config and rule mutations require a private SSO route not on the public Open-API. This view is read-only.')}
      </Alert>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <span style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
          {t('waap_domain.api_prot_discovery_title', 'Discovery Rules')}
        </span>
        <Badge>{rules.length}</Badge>
      </div>
      {rules.length === 0 ? (
        <EmptyState
          title={t('waap_domain.api_prot_discovery_empty', 'No discovery rules')}
          description={t('waap_domain.api_prot_discovery_empty_desc', 'No API discovery rules configured for this account.')}
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {rules.map((rule, i) => {
            const r = rule as Record<string, unknown>;
            return (
              <Card key={String(r.id ?? i)}>
                <CardBody style={{ fontSize: 'var(--vn-font-size-sm)' }}>
                  <pre style={{ margin: 0, fontSize: 'var(--vn-font-size-xs)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(r, null, 2)}
                  </pre>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Threat Detection section ────────────────────────────────────────────── */

function ThreatDetectionSection(_props: { anchorId: string }) {
  const { t } = useTranslation();

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <Alert tone="info" style={{ fontSize: 'var(--vn-font-size-xs)' }}>
        {t('waap_domain.api_prot_threat_note', 'Threat detection charts and rule mutations use analytics/threat-detection endpoints (charts) and private rule routes. Chart rendering requires the Partner chart engine; raw chart data is shown in the Analytics tab.')}
      </Alert>
      <EmptyState
        title={t('waap_domain.api_prot_threat_title', 'Threat Detection')}
        description={t('waap_domain.api_prot_threat_desc', 'Use the Analytics tab → API sub-board to view threat detection charts. Per-rule mutations require the Partner console.')}
      />
    </div>
  );
}

/* ── DLP section ─────────────────────────────────────────────────────────── */

interface DlpPending {
  rule: DlpRule;
  config: DlpRuleConfig;
}

function DlpRuleEditor({
  anchorId,
  rule,
  onDone,
}: {
  anchorId: string;
  rule: DlpRule;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [configDraft, setConfigDraft] = useState(
    JSON.stringify(rule.config ?? {}, null, 2),
  );
  const [pending, setPending] = useState<DlpPending | null>(null);

  function parseConfig(): DlpRuleConfig {
    try { return JSON.parse(configDraft) as DlpRuleConfig; }
    catch { return rule.config; }
  }

  function handleSave() {
    setPending({ rule, config: parseConfig() });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>
          {t('waap_domain.api_prot_dlp_edit_title', 'Edit DLP Rule: {{name}}', {
            name: rule.name ?? String(rule.id ?? ''),
          })}
        </CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <Field
          label={t('waap_domain.api_prot_dlp_config', 'Rule Config (JSON)')}
          description={t('waap_domain.api_prot_dlp_config_desc', 'Fields: pii_type, class, pii_level, enabled, req_enabled, is_pii_masked, is_pii_resp_block, mask_all_api, mask_specified_api, pre_n, last_m, whitelist[]')}
        >
          {(p) => (
            <Textarea
              {...p}
              value={configDraft}
              onChange={(e) => setConfigDraft(e.target.value)}
              rows={10}
              style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)' }}
            />
          )}
        </Field>
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="secondary" onClick={onDone}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button size="sm" variant="primary" onClick={handleSave}>
            {t('common.save', 'Save')}
          </Button>
        </div>
      </CardBody>

      {pending && (
        <ActAsMutationConfirm
          open
          onOpenChange={(open) => { if (!open) setPending(null); }}
          title={t('waap_domain.api_prot_dlp_save_title', 'Update DLP rule')}
          summary={t('waap_domain.api_prot_dlp_save_summary', 'Update DLP rule config for rule #{{id}}.', { id: rule.id ?? '?' })}
          destructive={false}
          queryKeysToInvalidate={[
            actAsWAAPKeys.aspect(anchorId, 'api-protection/data-loss/rules'),
          ]}
          mutationFn={() =>
            actAsClient.put(
              `/waap/domains/${anchorId}/api-protection/data-loss/rules/${rule.id}`,
              { ...rule, config: pending.config },
            )
          }
          onSuccess={() => { setPending(null); onDone(); }}
        />
      )}
    </Card>
  );
}

function DlpSection({ anchorId }: { anchorId: string }) {
  const { t } = useTranslation();
  const dlpQ = useActAsWAAPDlpRules(anchorId);
  const [editingRule, setEditingRule] = useState<DlpRule | null>(null);

  if (dlpQ.isLoading) return <Skeleton style={{ width: '100%', height: 100 }} />;
  if (dlpQ.isError) {
    return (
      <TabError
        title={t('waap_domain.api_prot_dlp_load_error', 'Failed to load DLP rules')}
        onRetry={() => dlpQ.refetch()}
      />
    );
  }

  const rules = dlpQ.data ?? [];

  if (editingRule) {
    return (
      <DlpRuleEditor
        anchorId={anchorId}
        rule={editingRule}
        onDone={() => { setEditingRule(null); dlpQ.refetch(); }}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <span style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
          {t('waap_domain.api_prot_dlp_title', 'Data Loss Prevention Rules')}
        </span>
        <Badge>{rules.length}</Badge>
      </div>

      {rules.length === 0 ? (
        <EmptyState
          title={t('waap_domain.api_prot_dlp_empty', 'No DLP rules')}
          description={t('waap_domain.api_prot_dlp_empty_desc', 'No data loss prevention rules configured for this account.')}
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {rules.map((rule) => (
            <Card key={rule.id ?? rule.name}>
              <CardBody style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
                    {rule.name ?? `Rule #${rule.id}`}
                  </div>
                  {rule.config?.pii_type && (
                    <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 2 }}>
                      {t('waap_domain.api_prot_dlp_pii', 'PII: {{type}}', { type: String(rule.config.pii_type) })}
                      {rule.config?.enabled != null && (
                        <> · {rule.config.enabled
                          ? t('waap_domain.api_prot_dlp_enabled', 'Enabled')
                          : t('waap_domain.api_prot_dlp_disabled', 'Disabled')}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setEditingRule(rule)}
                >
                  {t('common.edit', 'Edit')}
                </Button>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Vulnerability section ───────────────────────────────────────────────── */

function VulnerabilitySection({ anchorId }: { anchorId: string }) {
  const { t } = useTranslation();
  const chartsQ = useActAsWAAPVulnerabilityCharts(anchorId);

  if (chartsQ.isLoading) return <Skeleton style={{ width: '100%', height: 80 }} />;
  if (chartsQ.isError) {
    return (
      <TabError
        title={t('waap_domain.api_prot_vuln_error', 'Failed to load vulnerability charts')}
        onRetry={() => chartsQ.refetch()}
      />
    );
  }

  const charts = chartsQ.data?.charts ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <Alert tone="info" style={{ fontSize: 'var(--vn-font-size-xs)' }}>
        {t('waap_domain.api_prot_vuln_note', 'Vulnerability detection rules and per-vulnerability detail require private SSO routes. This section shows chart data only.')}
      </Alert>
      {charts.length === 0 ? (
        <EmptyState
          title={t('waap_domain.api_prot_vuln_empty', 'No vulnerability data')}
          description={t('waap_domain.api_prot_vuln_empty_desc', 'Vulnerability detection charts are not yet available for this account.')}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--vn-space-3)' }}>
          {charts.map((c, i) => {
            const chart = c as Record<string, unknown>;
            return (
              <Card key={String(chart.key ?? i)}>
                <CardBody>
                  <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                    {String(chart.title ?? chart.key ?? '')}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
                    {chart.kind === 'number' ? String(chart.value ?? '—') : String(chart.kind ?? '')}
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Root component ──────────────────────────────────────────────────────── */

type ApiProtSection = 'overview' | 'discovery' | 'threat' | 'dlp' | 'vulnerability';

interface WaapApiProtectionTabProps {
  anchorId: string;
}

/**
 * API Protection tab — 5 sections as inner sub-tabs.
 * Only DLP rules section has mutations (PUT per rule config).
 * Overview, Discovery, Threat Detection, Vulnerability are read-only.
 */
export function WaapApiProtectionTab({ anchorId }: WaapApiProtectionTabProps) {
  const { t } = useTranslation();
  const [section, setSection] = useState<ApiProtSection>('overview');

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <Shield size={15} style={{ color: 'var(--vn-fg-muted)' }} />
        <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {t('waap_domain.api_prot_heading', 'API Protection — account-level. DLP rules are editable; other sections are read-only.')}
        </span>
      </div>

      <Tabs value={section} onValueChange={(v) => setSection(v as ApiProtSection)}>
        <TabsList>
          <TabsTrigger value="overview">
            {t('waap_domain.api_prot_tab_overview', 'Overview')}
          </TabsTrigger>
          <TabsTrigger value="discovery">
            {t('waap_domain.api_prot_tab_discovery', 'Discovery')}
          </TabsTrigger>
          <TabsTrigger value="threat">
            {t('waap_domain.api_prot_tab_threat', 'Threat Detection')}
          </TabsTrigger>
          <TabsTrigger value="dlp">
            {t('waap_domain.api_prot_tab_dlp', 'Data Loss Prevention')}
          </TabsTrigger>
          <TabsTrigger value="vulnerability">
            {t('waap_domain.api_prot_tab_vuln', 'Vulnerability')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {section === 'overview' && <OverviewSection anchorId={anchorId} />}
        </TabsContent>

        <TabsContent value="discovery">
          {section === 'discovery' && <DiscoverySection anchorId={anchorId} />}
        </TabsContent>

        <TabsContent value="threat">
          {section === 'threat' && <ThreatDetectionSection anchorId={anchorId} />}
        </TabsContent>

        <TabsContent value="dlp">
          {section === 'dlp' && <DlpSection anchorId={anchorId} />}
        </TabsContent>

        <TabsContent value="vulnerability">
          {section === 'vulnerability' && <VulnerabilitySection anchorId={anchorId} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
